import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import Footer from '../offer-package/footer';
import Header from '../offer-package/header';
import Overlay from '../ui/overlay';
import { INotificationService, NotificationService, SpecialPackageApi } from '../services';
import { StyledLink } from '../ui';
import { ISpecialPackageDto } from '../model';
import SpecialPackageCard from './package-card';
import {
  getAvailableSpecialPackageOccupancies,
  getDefaultSpecialPackageOccupancy,
  SpecialPackageOccupancy,
} from './occupancy';
import SpecialPackageForm from './form';
import SpecialPackageUnavailableState, {
  SpecialPackageUnavailableKind,
} from './unavailable-state';
import { useSpecialPackageReferences } from './use-special-package-references';

const notificationService: INotificationService = new NotificationService();

interface IApiProblemDetails {
  status?: number;
  title?: string;
  detail?: string;
  type?: string;
  instance?: string;
}

interface ISpecialPackageUnavailableState {
  kind: SpecialPackageUnavailableKind;
  title: string;
  description: string;
  arrival?: string;
  departure?: string;
}

function mapProblemDetailsToUnavailableState(
  problem: IApiProblemDetails | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): ISpecialPackageUnavailableState {
  const detail = problem?.detail ?? '';
  const unavailableRangeMatch = detail.match(
    /because no rooms are currently available between '(\d{4}-\d{2}-\d{2})' and '(\d{4}-\d{2}-\d{2})'$/i
  );

  if (unavailableRangeMatch) {
    return {
      kind: 'no-rooms',
      title: t('special.unavailable.no-rooms.title'),
      description: t('special.unavailable.no-rooms.description'),
      arrival: unavailableRangeMatch[1],
      departure: unavailableRangeMatch[2],
    };
  }

  if (detail.includes('because the package is not active')) {
    return {
      kind: 'inactive',
      title: t('special.unavailable.inactive.title'),
      description: t('special.unavailable.inactive.description'),
    };
  }

  return {
    kind: 'generic',
    title: t('special.unavailable.generic.title'),
    description: t('special.unavailable.generic.description'),
  };
}

export function SpecialDetailPage() {
  const { t, i18n } = useTranslation('offer-package');
  const { id } = useParams();
  const location = useLocation();
  const specialPackageApi = new SpecialPackageApi();
  const bookingRef = useRef<HTMLDivElement>(null);
  const [showBooking, setShowBooking] = useState(false);
  const [specialPackage, setSpecialPackage] = useState<ISpecialPackageDto>();
  const [unavailableState, setUnavailableState] =
    useState<ISpecialPackageUnavailableState | null>(null);
  const [selectedOccupancy, setSelectedOccupancy] = useState<number | null>(
    location.state?.selectedOccupancy ?? null
  );
  const showBackLink = location.state?.fromSpecialList === true;

  const parsedId = Number.parseInt(id ?? '', 10);

  if (Number.isNaN(parsedId)) {
    throw new Error('SpecialPackage-ID not valid');
  }

  useEffect(() => {
    let isMounted = true;

    setSpecialPackage(undefined);
    setUnavailableState(null);

    specialPackageApi
      .getActiveById(parsedId)
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setSpecialPackage(data);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        if (axios.isAxiosError<IApiProblemDetails>(error)) {
          setUnavailableState(
            mapProblemDetailsToUnavailableState(error.response?.data, t)
          );
          return;
        }

        setUnavailableState(
          mapProblemDetailsToUnavailableState(undefined, t)
        );
      });

    return () => {
      isMounted = false;
    };
  }, [parsedId, t]);

  useEffect(() => {
    if (!specialPackage) {
      return;
    }

    const availableOccupancies =
      getAvailableSpecialPackageOccupancies(specialPackage);

    if (
      availableOccupancies.includes(
        selectedOccupancy as SpecialPackageOccupancy
      )
    ) {
      return;
    }

    setSelectedOccupancy(getDefaultSpecialPackageOccupancy(specialPackage));
  }, [selectedOccupancy, specialPackage]);

  useEffect(() => {
    if (showBooking) {
      bookingRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showBooking]);

  const references = useSpecialPackageReferences(specialPackage);

  if (unavailableState) {
    return (
      <SpecialPackageUnavailableState
        language={i18n.language}
        kind={unavailableState.kind}
        title={unavailableState.title}
        description={unavailableState.description}
        arrival={unavailableState.arrival}
        departure={unavailableState.departure}
      ></SpecialPackageUnavailableState>
    );
  }

  if (!specialPackage) {
    return <p>{t('special.shared.loading')}</p>;
  }

  return (
    <div>
      <Header></Header>

      <main className="container max-w-5xl px-2 sm:px-0 pb-24">
        {showBackLink && (
          <div className="pt-10">
            <StyledLink href={`/${i18n.language}/special`}>
              {t('special.detail.back')}
            </StyledLink>
          </div>
        )}

        <div className={showBackLink ? 'mt-6' : 'pt-10'}>
          <SpecialPackageCard
            specialPackage={specialPackage}
            references={references}
            selectedOccupancy={selectedOccupancy}
            onOccupancyChange={setSelectedOccupancy}
            onSelect={() => setShowBooking(true)}
            actionLabel={t('shared.book')}
          ></SpecialPackageCard>
        </div>

        {showBooking && (
          <div ref={bookingRef} className="mt-12">
            <div className="max-w-4xl">
              <h2 className="font-serif text-3xl text-brand-cashmere-900">
                {t('special.booking.title')}
              </h2>
              <p className="mt-3 text-brand-cashmere-800">
                {t('special.booking.description')}
              </p>
            </div>

            <div className="mt-8">
              <SpecialPackageForm
                specialPackage={specialPackage}
                references={references}
                notificationService={notificationService}
                initialOccupancy={selectedOccupancy}
              ></SpecialPackageForm>
            </div>
          </div>
        )}
      </main>

      <Footer></Footer>

      <Overlay service={notificationService}></Overlay>
    </div>
  );
}

export default SpecialDetailPage;
